import { TreadAgentVisualization } from '~hooks/useTreadAgent/models';
import {
  ActiveTurn,
  TreadAgentChannelEvent,
  TreadAgentTraceItem,
  TreadAgentTraceReasoning,
} from '~tread-agent/types';

export function createEmptyTurn(): ActiveTurn {
  return {
    status: 'idle',
    text: '',
    errorMessage: null,
    finalMessageId: null,
    isStopRequested: false,
    trace: [],
    visualizations: [],
  };
}

// A brand-new streaming turn with no text yet. Used both on turn_started and when the consumer
// optimistically opens a turn on send (before the first server event arrives).
export function createStreamingTurn(): ActiveTurn {
  return {
    status: 'streaming',
    text: '',
    errorMessage: null,
    finalMessageId: null,
    isStopRequested: false,
    trace: [],
    visualizations: [],
  };
}

function trailingOpenReasoning(trace: TreadAgentTraceItem[]) {
  const last = trace.at(-1);

  return last?.kind === 'reasoning' && last.isOpen ? last : null;
}

function closeTrailingReasoning(trace: TreadAgentTraceItem[]) {
  const open = trailingOpenReasoning(trace);

  if (!open) {
    return trace;
  }

  return [...trace.slice(0, -1), { ...open, isOpen: false }];
}

function appendReasoningDelta(trace: TreadAgentTraceItem[], delta: string) {
  const open = trailingOpenReasoning(trace);

  if (open) {
    return [...trace.slice(0, -1), { ...open, text: open.text + delta }];
  }

  const block: TreadAgentTraceReasoning = {
    kind: 'reasoning',
    id: `reasoning-${trace.length}`,
    text: delta,
    isOpen: true,
  };

  return [...trace, block];
}

// Marks the most recent still-running step done or errored. tool_call_finished carries no tool
// call id, so the pairing is positional; the backend runs one tool call at a time per turn.
function resolveTrailingStep(trace: TreadAgentTraceItem[], errored: boolean) {
  const status = errored ? ('errored' as const) : ('done' as const);

  for (let index = trace.length - 1; index >= 0; index -= 1) {
    const item = trace[index];

    if (item.kind === 'step' && item.status === 'running') {
      const resolved = [...trace];
      resolved[index] = { ...item, status };

      return resolved;
    }
  }

  return trace;
}

/**
 * Pure fold of one backend channel event into the live turn. No side effects, no MobX — this is
 * the protocol implementation and is unit-tested in isolation.

 */
export function reduceActiveTurn(
  turn: ActiveTurn,
  event: TreadAgentChannelEvent,
): ActiveTurn {
  switch (event.type) {
    case 'turn_started':
      // beginStreamingTurn already opened this turn on the send POST, so a stop clicked in
      // the gap before this event must survive it.
      return { ...createStreamingTurn(), isStopRequested: turn.isStopRequested };

    case 'message_delta':
      return { ...turn, status: 'streaming', text: turn.text + event.delta };

    case 'thinking_delta':
      return {
        ...turn,
        status: 'streaming',
        trace: appendReasoningDelta(turn.trace, event.delta),
      };

    case 'thinking_finished':
      return { ...turn, trace: closeTrailingReasoning(turn.trace) };

    // The backend persists only the text after the last tool call, so drop earlier text here too.
    case 'tool_call_started':
      return {
        ...turn,
        status: 'streaming',
        text: '',
        trace: [
          // Defensive: the backend flushes reasoning before a tool call, but a dropped
          // thinking_finished would otherwise let the next reasoning block append to this one.
          ...closeTrailingReasoning(turn.trace),
          {
            kind: 'step',
            id: `step-${turn.trace.length}`,
            activity: event.activity ?? null,
            activityKind: event.activity_kind ?? null,
            status: 'running',
          },
        ],
      };

    case 'tool_call_finished':
      return { ...turn, trace: resolveTrailingStep(turn.trace, event.errored) };

    // The channel can redeliver an event after a reconnect, so a chart already on the turn is
    // not added twice.
    case 'visualization':
      return turn.visualizations.some(({ id }) => id === event.visualization.id)
        ? turn
        : {
            ...turn,
            visualizations: [
              ...turn.visualizations,
              TreadAgentVisualization.parse(event.visualization),
            ],
          };

    // A stop is not a failure — the backend persists the partial reply, so a cancelled turn
    // ends exactly like a completed one. A step still running when the user stopped stays
    // unmarked rather than being called errored: nothing went wrong, it was abandoned.
    case 'turn_cancelled':
    case 'turn_done':
      return {
        ...turn,
        status: 'idle',
        finalMessageId: event.final_message_id,
        isStopRequested: false,
        trace: closeTrailingReasoning(turn.trace),
      };

    case 'turn_error':
      return {
        ...turn,
        status: 'error',
        errorMessage: event.error,
        isStopRequested: false,
        trace: resolveTrailingStep(closeTrailingReasoning(turn.trace), true),
      };

    default:
      // Entity attachment, the pending confirmation card, the conversation retitle, and any
      // event a newer backend adds don't affect the live turn.
      return turn;
  }
}
